/**
 * Auto-scheduling for the Gantt (task_55l7tk). When one task is rescheduled,
 * push its dependents forward so none starts before a blocker finishes
 * ("no negative slack"), preserving each task's duration. Returns the list of
 * shifts to persist — the moved task itself is not included.
 *
 * Convention: a dependent may start on or after its blocker's due date
 * (finish-to-start at the boundary). We only ever push tasks *later* to resolve
 * a violation — never pull them earlier (that would silently compact a plan the
 * user laid out by hand).
 *
 * Locked tasks (startLocked) are never moved; their (possibly violating) due
 * date still constrains their own dependents, so a locked task in the middle of
 * a chain leaves a visible overlap — that overlap is the conflict signal.
 *
 * Cycles can't occur — the dependency endpoint rejects them — so a Kahn
 * topological order always exists, which guarantees a pushed task's new due is
 * seen by its own dependents (multi-level cascade in a single pass).
 */
export type SchedTask = {
  id: string;
  startDate: string | null;
  dueDate: string | null;
  startLocked?: boolean | number;
  parentTaskId?: string | null;
};
export type SchedEdge = { blockerId: string; blockedId: string };
export type SchedShift = { id: string; startDate: string; dueDate: string };

const DAY = 86_400_000;

// Parse a date-only string to a UTC-midnight epoch and back, consistently, so
// day arithmetic never drifts across timezones.
function toEpoch(s: string): number {
  return Date.parse(s);
}
function toYmd(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

export function autoSchedule(
  tasks: SchedTask[],
  edges: SchedEdge[],
  movedId: string,
  movedStart: string,
  movedDue: string,
): SchedShift[] {
  // Any task with at least one date has a bar and can participate. Effective
  // start/due mirror how the chart plots a bar: a due-only task is a
  // zero-duration bar at its due date (start ?? due / due ?? start).
  const start = new Map<string, number>();
  const due = new Map<string, number>();
  const locked = new Map<string, boolean>();
  for (const t of tasks) {
    const s = t.startDate ?? t.dueDate;
    const d = t.dueDate ?? t.startDate;
    if (s && d) {
      start.set(t.id, toEpoch(s));
      due.set(t.id, toEpoch(d));
      locked.set(t.id, Boolean(t.startLocked));
    }
  }
  if (!start.has(movedId)) return [];
  // Apply the move.
  start.set(movedId, toEpoch(movedStart));
  due.set(movedId, toEpoch(movedDue));

  // Edges restricted to dated endpoints; build adjacency + predecessor maps.
  const adj = new Map<string, string[]>(); // blocker -> [blocked]
  const preds = new Map<string, string[]>(); // blocked -> [blockers]
  const indeg = new Map<string, number>();
  for (const id of start.keys()) indeg.set(id, 0);
  for (const e of edges) {
    if (!start.has(e.blockerId) || !start.has(e.blockedId)) continue;
    (adj.get(e.blockerId) ?? adj.set(e.blockerId, []).get(e.blockerId)!).push(
      e.blockedId,
    );
    (
      preds.get(e.blockedId) ?? preds.set(e.blockedId, []).get(e.blockedId)!
    ).push(e.blockerId);
    indeg.set(e.blockedId, (indeg.get(e.blockedId) ?? 0) + 1);
  }

  // Kahn topological order.
  const queue = [...indeg].filter(([, d]) => d === 0).map(([id]) => id);
  const order: string[] = [];
  while (queue.length) {
    const id = queue.shift()!;
    order.push(id);
    for (const n of adj.get(id) ?? []) {
      const d = indeg.get(n)! - 1;
      indeg.set(n, d);
      if (d === 0) queue.push(n);
    }
  }

  // Forward pass: push each task to start no earlier than its latest blocker due.
  const shifts: SchedShift[] = [];
  for (const id of order) {
    if (id === movedId || locked.get(id)) continue;
    const ps = preds.get(id);
    if (!ps || ps.length === 0) continue;
    let earliest = -Infinity;
    for (const p of ps) earliest = Math.max(earliest, due.get(p)!);
    const curStart = start.get(id)!;
    if (earliest > curStart) {
      const dur = due.get(id)! - curStart; // preserve duration (ms)
      start.set(id, earliest);
      due.set(id, earliest + dur);
      shifts.push({
        id,
        startDate: toYmd(earliest),
        dueDate: toYmd(earliest + dur),
      });
    }
  }
  return shifts;
}

// Re-exported so callers can express durations in days if needed.
export const MS_PER_DAY = DAY;
